import { CheckCircle2, Circle, Info, Loader2 } from "lucide-react";
import React from "react";

import { cn } from "@/app/handlers/cn.utils";

const steps = [
	{ label: "Initializing" },
	{ label: "Checking Education" },
	{ label: "Checking Experience" },
	{ label: "Checking Certifications" },
	{ label: "Reviewing Key Skills" },
	{ label: "Spotting Your Strengths" },
	{ label: "Highlighting Focus Areas" },
	{ label: "Preparing Guidance" },
	{ label: "Preparing Insights" },
];

interface BulkFitCheckLoadingProps {
	totalJobs: number;
	progress: number;
	currentJob: string;
	dspTitle: string;
}

function BulkFitCheckLoading({ totalJobs, progress, dspTitle }: BulkFitCheckLoadingProps) {
	const [loadingIndex, setLoadingIndex] = React.useState(0);

	React.useEffect(() => {
		if (loadingIndex < steps.length - 1) {
			const timer = setTimeout(
				() => {
					setLoadingIndex((prev) => prev + 1);
				},
				1500 + Math.random() * 1000,
			);
			return () => clearTimeout(timer);
		}
	}, [loadingIndex]);

	// Combine step progress with overall progress
	const stepProgress = Math.round(((loadingIndex + 1) / steps.length) * 100);
	const displayProgress = Math.max(progress, Math.min(stepProgress, 95));

	return (
		<div>
			<h2 className="text-xl font-bold text-gray-900 mb-1">
				Analyzing {totalJobs} job{totalJobs !== 1 ? "s" : ""} against {dspTitle} DSP…
			</h2>
			<p className="text-gray-600 text-sm mb-6">
				We&apos;re reviewing your resume against {totalJobs} job{totalJobs !== 1 ? "s" : ""} to highlight your
				strengths, gaps, and focus areas.
			</p>

			{/* Progress Bar */}
			<div className="flex items-center gap-3 mb-6">
				<div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
					<div
						className="h-2 bg-blue-600 rounded-full transition-all duration-500"
						style={{ width: `${displayProgress}%` }}
					/>
				</div>
				<span className="text-sm font-medium text-gray-700 min-w-[40px]">{displayProgress}%</span>
			</div>

			<div className="mb-4">
				<h3 className="text-base font-semibold text-gray-900 mb-1">Analysis Steps</h3>
				<p className="text-gray-500 text-sm mb-4">Hang tight! This won&apos;t take long...</p>

				<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
					{steps.map((step, index) => {
						const status = index < loadingIndex ? "done" : index === loadingIndex ? "loading" : "pending";
						return (
							<div key={step.label} className="flex items-center gap-1.5">
								{status === "done" && <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />}
								{status === "loading" && <Loader2 className="w-4 h-4 text-gray-500 animate-spin shrink-0" />}
								{status === "pending" && <Circle fill="#e9ecef" className="w-4 h-4 text-gray-200 shrink-0" />}
								<span
									className={cn("text-sm", {
										"text-gray-800": status === "done" || status === "loading",
										"text-gray-500": status === "pending",
									})}
								>
									{step.label}
								</span>
							</div>
						);
					})}
				</div>
			</div>

			<div className="mt-8 bg-blue-50 rounded-lg px-4 py-3 flex items-start gap-3">
				<Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
				<span className="text-sm text-blue-900">
					<span className="font-semibold">Pro Tip:</span> Your FitCheck results will be saved to your dashboard for
					future reference!
				</span>
			</div>
		</div>
	);
}

export default BulkFitCheckLoading;
